import React, { FC, useContext } from "react";
import { Form } from "react-final-form";
import { useRouter } from "next/router";
import { Button } from "@pepdirect/ui/button";
import { Heading } from "@pepdirect/ui/heading";
import { useIsMobile } from "@pepdirect/helpers/useIsMobile";
import { SimpleDropdown } from "components/Fields/SimpleDropdown";
import {
  OrderSubscriptionStatusNames,
  useUpdateSubscriptionStatusMutation,
} from "services/graphql/generated";
import { CancelSubscriptionProps } from "types/modal";
import st from "./cancelSubscriptionModal.module.scss";
import { ReactSelectDropdownOption } from "constants/dropdown";
import { ModalContext } from "context/modal";

interface CancelSubscriptionModalProps extends CancelSubscriptionProps {
  onClose: () => void;
}

const dropdownFieldName = "cancelReason";

interface CancelSubscriptionFormValues {
  [dropdownFieldName]?: ReactSelectDropdownOption;
}

const reasonsForCanceling = [
  {
    label: "Need to start a new subscription",
    value: "NEED_TO_START_A_NEW_SUBSCRIPTION",
  },
  { label: "Don't need anymore", value: "DONT_NEED_ANYMORE" },
  { label: "Shipping or delivery issue", value: "SHIPPING_OR_DELIVERY_ISSUE" },
  { label: "Other", value: "OTHER" },
];

export const CancelSubscriptionModal: FC<CancelSubscriptionModalProps> = ({
  subscriptionId,
}) => {
  const { close } = useContext(ModalContext);
  const [updateSubscription, { loading }] =
    useUpdateSubscriptionStatusMutation();
  const isMobile = useIsMobile();
  const router = useRouter();

  const onSubmit = async (formObj: CancelSubscriptionFormValues) => {
    if (subscriptionId) {
      try {
        await updateSubscription({
          variables: {
            input: {
              id: subscriptionId,
              status: OrderSubscriptionStatusNames.Canceled,
              statusReason:
                formObj[dropdownFieldName]?.value || "USER_INITIATED_CANCEL",
            },
          },
        });
        close();
        await router.push("/dashboard/subscriptions");
      } catch (e) {
        // logged in errorLink
      }
    }
  };

  return (
    <div className={st.wrapper}>
      <div className={st.title}>
        <Heading level="h2" size="xl">
          Cancel this subscription?
        </Heading>
      </div>
      <div>This will cancel all future shipments of this subscription</div>
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit} className={st.form}>
              <SimpleDropdown
                name={dropdownFieldName}
                autoComplete=""
                options={reasonsForCanceling}
                label="Reason for canceling"
                styles={{
                  textAlign: "left",
                  width: isMobile ? "100%" : "70%",
                }}
              />
              <div className={st.buttons}>
                <Button
                  loading={loading}
                  onClick={handleSubmit}
                  htmlType="submit"
                >
                  Cancel Subscription
                </Button>
                <Button type="cancel" onClick={close}>
                  Back
                </Button>
              </div>
            </form>
          );
        }}
      />
    </div>
  );
};
